import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence, sendPasswordResetEmail, OAuthProvider } from "firebase/auth";
import {
  getRemoteConfig,
  getValue,
  fetchAndActivate
} from "firebase/remote-config";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { store } from './store';
import { setRemoteConfig } from './reducers/remoteConfigReducer';
import { devLog, devConfig, logError } from '../utils/logger';
import { trackEvent } from '../utils/analytics';

// ============================================================================
// GLOBAL ERROR HANDLER: Sopprimi errori Firebase "app-offline" in sviluppo
// Questi errori sono normali quando Firebase Installations non può connettersi
// ============================================================================
if (typeof window !== 'undefined') {
  // Handler per unhandled promise rejections (errori async Firebase)
  const originalOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    const error = event?.reason;
    const isFirebaseOfflineError =
      error?.code === 'installations/app-offline' ||
      error?.message?.includes('app-offline') ||
      error?.message?.includes('Application offline');

    if (isFirebaseOfflineError) {
      // Previeni che l'errore appaia nella console come "Unhandled"
      event.preventDefault();
      // Log solo in development per debug
      if (process.env.NODE_ENV === 'development') {
        console.debug('[Firebase] Offline mode - alcune funzionalità potrebbero essere limitate');
      }
      return;
    }

    // Per altri errori, usa l'handler originale se esiste
    if (originalOnUnhandledRejection) {
      return originalOnUnhandledRejection.call(window, event);
    }
  };

  // Handler per errori generici (Script error, etc.)
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    const isFirebaseOfflineError =
      error?.code === 'installations/app-offline' ||
      error?.message?.includes('app-offline') ||
      message?.includes?.('app-offline');

    if (isFirebaseOfflineError) {
      // Sopprimi l'errore
      return true;
    }

    // Per altri errori, usa l'handler originale se esiste
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };
}

// Verifica che le variabili d'ambiente siano definite
const checkEnvVariables = () => {
  const requiredVars = [
    "REACT_APP_FIREBASE_API_KEY",
    "REACT_APP_FIREBASE_AUTH_DOMAIN",
    "REACT_APP_FIREBASE_PROJECT_ID",
    "REACT_APP_FIREBASE_STORAGE_BUCKET",
    "REACT_APP_FIREBASE_MESSAGING_SENDER_ID",
    "REACT_APP_FIREBASE_APP_ID",
    "REACT_APP_FIREBASE_MEASUREMENT_ID",
  ];

  const missingVars = requiredVars.filter((varName) => !process.env[varName]);

  if (missingVars.length > 0) {
    console.error("Variabili d'ambiente mancanti:", missingVars);
    return false;
  }

  return true;
};

// Funzione per rilevare Safari
const isSafari = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes("safari") && !userAgent.includes("chrome");
};

let firebaseConfig;

if (process.env.REACT_APP_ENVIRONMENT === "production") {
  firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };
} else if (process.env.REACT_APP_ENVIRONMENT === "preproduction") {
  // Use the preprod firebase config
  firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY_PREPROD,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_PREPROD,
    databaseURL: process.env.REACT_APP_DATABASE_URL_PREPROD,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_PREPROD,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_PREPROD,
    messagingSenderId:
      process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_PREPROD,
    appId: process.env.REACT_APP_FIREBASE_APP_ID_PREPROD,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID_PREPROD,
  };
} else {
  // Development environment - use the values specified in .env.local
  firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };
}

let app;
let firestore;
let auth;

// ============================================================================
// PERF (LCP/INP): storage, analytics e remote-config NON sono necessari per il
// primo render né per l'auth. Vengono inizializzati pigramente (lazy) alla
// prima necessità invece che al boot, così la pipeline di boot non paga il
// loro costo (Installations / IndexedDB / istanze extra) sul critical path.
// ----------------------------------------------------------------------------
let storage;        // popolato lazy da getStorageInstance()
let analytics;      // popolato lazy da getAnalyticsInstance()
let _remoteConfig;  // popolato lazy da getRemoteConfigInstance()

// Helper per verificare se l'errore è "app-offline" (comune in sviluppo locale)
const isOfflineError = (error) => {
  return error?.code === 'installations/app-offline' ||
         error?.code === 'remoteconfig/storage-open' ||
         error?.message?.includes('app-offline') ||
         error?.message?.includes('Application offline') ||
         error?.message?.includes('storage-open') ||
         error?.message?.includes('IndexedDB');
};

try {
  // Verifica che le variabili d'ambiente siano definite
  if (!checkEnvVariables()) {
    throw new Error(
      "Variabili d'ambiente mancanti per la configurazione di Firebase"
    );
  }

  app = initializeApp(firebaseConfig);

  // NOTE (PERF): Remote Config NON è più inizializzato qui al boot.
  // Viene creato lazy in getRemoteConfigInstance() / fetchAndActivateConfig(),
  // così non tocca IndexedDB sul critical path del primo render.

  // Funzione per rilevare browser in-app (TikTok, Instagram, Facebook)
  const isInAppBrowser = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('instagram') ||
           userAgent.includes('fban') ||
           userAgent.includes('fbav') ||
           userAgent.includes('tiktok') ||
           userAgent.includes('snapchat');
  };

  // Configurazione Firestore: scelta cache in base al motore browser.
  // Tutti i WKWebView (Safari macOS/iOS, Mobile Safari, Chrome iOS, in-app
  // browser) usano memoryLocalCache: WebKit chiude la connessione IDB durante
  // background lungo / memory pressure su iOS, generando l'errore non
  // recuperabile "UnknownError: Connection to Indexed Database server lost".
  // memoryLocalCache non usa IndexedDB → il problema è impossibile per
  // costruzione. Su Chrome/Firefox/Edge desktop manteniamo persistentLocalCache
  // per offline support e cold-start veloce.
  const useMemoryCache = isInAppBrowser() || isSafari();
  const firestoreConfig = useMemoryCache
    ? {
        // WKWebView: cache in memoria (evita IDB lost + problemi DB deletion)
        experimentalForceLongPolling: true,
        localCache: memoryLocalCache({
          garbageCollector: {
            cacheSizeBytes: 50 * 1024 * 1024
          }
        })
      }
    : {
        // Browser desktop standard: cache persistente con multi-tab
        localCache: persistentLocalCache({
          cacheSizeBytes: 100 * 1024 * 1024,
          tabManager: persistentMultipleTabManager()
        })
      };

  // Filtra i log verbose di Firestore per ridurre rumore in console
  // I messaggi "Failed to obtain primary lease" sono warning benigni del sistema multi-tab
  const originalConsoleLog = console.log;
  console.log = function(...args) {
    // Filtra solo i log di Firestore relativi al primary lease
    if (args[0] && typeof args[0] === 'string' &&
        args[0].includes('@firebase/firestore') &&
        args[0].includes('Failed to obtain primary lease')) {
      // Silenzia questi log specifici (sono normali con multi-tab)
      return;
    }
    originalConsoleLog.apply(console, args);
  };

  // Inizializza Firestore con la configurazione appropriata
  firestore = initializeFirestore(app, firestoreConfig);

  // Log della configurazione applicata (solo in development)
  devLog("Firestore inizializzato con configurazione:", {
    browser: isInAppBrowser() ? 'In-App Browser' : isSafari() ? 'Safari/WKWebView' : 'Standard',
    cacheType: useMemoryCache ? 'Memory' : 'Persistent',
    multiTab: !useMemoryCache
  });

  // NOTE (PERF): Analytics NON è più inizializzato qui al boot.
  // Firebase Analytics richiede Firebase Installations (rete + IndexedDB) e non
  // serve al primo render. Viene creato lazy in getAnalyticsInstance().
  // L'event-tracking applicativo passa comunque da utils/analytics.js, che ha
  // la propria inizializzazione lazy + gtag.

  // Auth: necessario per il primo render / onAuthStateChanged → resta eager.
  auth = getAuth(app);
  setPersistence(auth, browserLocalPersistence).catch((err) => {
    logError("Errore impostando la persistenza auth:", err);
  });

  // NOTE (PERF): Storage NON è più inizializzato qui al boot.
  // Viene creato lazy alla prima lettura dell'export `storage` (vedi
  // getStorageInstance()), così la pipeline di boot non paga getStorage().
  devLog("Firebase è stato inizializzato correttamente.");
} catch (error) {
  if (isOfflineError(error)) {
    devLog("⚠️ Firebase Installations offline - alcune funzionalità potrebbero essere limitate");
  } else {
    logError("Errore durante l'inizializzazione di Firebase:", error);
  }
}

// ============================================================================
// LAZY GETTERS (PERF): storage / analytics / remote-config sono creati alla
// prima necessità, non al boot. Gli export con lo stesso nome restano stabili
// come live-binding; l'istanza viene popolata da questi getter.
// ============================================================================

/**
 * Restituisce (creandola al primo uso) l'istanza Firebase Storage.
 * Popola anche l'export live-binding `storage` per i consumer esistenti.
 */
export const getStorageInstance = () => {
  if (!storage && app) {
    try {
      storage = getStorage(app);
    } catch (e) {
      logError("Errore inizializzazione Firebase Storage:", e);
    }
  }
  return storage;
};

/**
 * Restituisce (creandola al primo uso) l'istanza Firebase Analytics.
 * Gestisce gracefully la modalità offline (Installations non disponibile).
 */
export const getAnalyticsInstance = async () => {
  if (analytics) return analytics;
  if (!app) return null;
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  if (!isOnline) {
    devLog("⚠️ Browser offline - Analytics disabilitato");
    return null;
  }
  try {
    // Import dinamico: tiene firebase/analytics fuori dal critical path di boot.
    const { getAnalytics } = await import("firebase/analytics");
    analytics = getAnalytics(app);
  } catch (analyticsError) {
    if (isOfflineError(analyticsError)) {
      devLog("⚠️ Analytics non disponibile (modalità offline) - l'app funzionerà comunque");
      analytics = null;
    } else {
      logError("Errore inizializzazione Firebase Analytics:", analyticsError);
      analytics = null;
    }
  }
  return analytics;
};

/**
 * Restituisce (creandola al primo uso) l'istanza Remote Config.
 */
const getRemoteConfigInstance = () => {
  if (_remoteConfig) return _remoteConfig;
  if (!app) return null;
  try {
    _remoteConfig = getRemoteConfig(app);
    // Fetch interval consigliato per produzione (1h qui, 12h default Firebase)
    _remoteConfig.settings.minimumFetchIntervalMillis = 3600000;
  } catch (rcError) {
    devLog('⚠️ Remote Config: inizializzazione fallita (IndexedDB non disponibile) - uso valori default');
    _remoteConfig = null;
  }
  return _remoteConfig;
};

// Popola l'export live-binding `storage` fuori dal critical path di boot, così
// i consumer che fanno `import { storage }` lo trovano pronto al primo upload,
// senza pagare getStorage() durante il first paint.
if (typeof window !== 'undefined') {
  const deferStorageInit = () => getStorageInstance();
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(deferStorageInit, { timeout: 2000 });
  } else {
    setTimeout(deferStorageInit, 0);
  }
}

/**
 * Imposta User-ID in Firebase Analytics per tracking cross-device
 * @param {string} userId - Firebase UID dell'utente
 */
export const setAnalyticsUserId = (userId) => {
  if (!userId || typeof window === 'undefined') {
    logError('setAnalyticsUserId: userId is required');
    return;
  }

  try {
    // Imposta User-ID in Firebase Analytics tramite gtag
    if (typeof window.gtag === 'function') {
      const measurementId = firebaseConfig.measurementId;

      window.gtag('config', measurementId, {
        'user_id': userId
      });

      devLog('✅ Analytics User-ID set:', userId);
    } else {
      logError('gtag is not available, User-ID not set');
    }
  } catch (error) {
    logError('Error setting Analytics User-ID:', error);
  }
};



// Fetch and activate the remote config values
const fetchAndActivateConfig = async () => {
  // Skip fetch se offline - usa valori cached/default
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  if (!isOnline) {
    devLog('⚠️ Remote Config: browser offline - uso valori default');
    store.dispatch(setRemoteConfig({
      uploadDocWithPreview: 'A'
    }));
    return;
  }

  try {
    const remoteConfig = getRemoteConfigInstance();
    if (!remoteConfig) {
      // Remote Config non disponibile (es. IndexedDB assente) → usa default
      store.dispatch(setRemoteConfig({
        uploadDocWithPreview: 'A'
      }));
      return;
    }

    // Usa Promise con catch esplicito per catturare errori async interni
    await fetchAndActivate(remoteConfig).catch((fetchError) => {
      if (isOfflineError(fetchError)) {
        devLog('⚠️ Remote Config: fetch fallito (offline)');
        return false; // Indica fallimento silenzioso
      }
      throw fetchError; // Rilancia altri errori
    });

    // Now we can safely get the value
    const val = getValue(remoteConfig, "UploadDoc_with_preview");
    devConfig('Remote Config value:', val.asString());
    // Store the value in Redux
    store.dispatch(setRemoteConfig({
      uploadDocWithPreview: val.asString()
    }));

    // Invia evento di analytics in base alla versione ricevuta
    // NOTA: NON usare "source" come parametro - è riservato da GA4 per la sorgente del traffico
    if (val.asString() === 'A') {
      trackEvent('ab_version_assigned', {
        ab_test_name: 'UploadDoc_with_preview',
        ab_variant: 'A',
        event_source: 'firebase_init'
      });
    } else if (val.asString() === 'B') {
      trackEvent('ab_version_assigned', {
        ab_test_name: 'UploadDoc_with_preview',
        ab_variant: 'B',
        event_source: 'firebase_init'
      });
    }

  } catch (error) {
    // Gestisci gracefully l'errore offline - Remote Config userà i valori cached o default
    if (isOfflineError(error)) {
      devLog('⚠️ Remote Config: modalità offline - uso valori cached/default');
    } else {
      logError('Error fetching Remote Config:', error);
    }
    // Usa sempre il valore default in caso di errore
    store.dispatch(setRemoteConfig({
      uploadDocWithPreview: 'A'
    }));
  }
};

// Call the fetch function con delay per permettere agli error handlers di inizializzarsi
setTimeout(() => {
  fetchAndActivateConfig().catch(() => {
    // Catch aggiuntivo per assicurarsi che nessun errore vada unhandled
  });
}, 100);

export {
  app,
  analytics,
  auth,
  sendPasswordResetEmail,
  OAuthProvider,
  firestore,
  storage,
  firebaseConfig,
};
